import includes from "lodash-es/includes";
import isArray from "lodash-es/isArray";

import { CONTENTFUL_ENV } from "../common/environment";
import { type CustomerFeature, FEATURE_NAME } from "../common/types/featureTypes";
import type { Invoice } from "../common/types/invoiceTypes";
import type { SupportedLocaleType } from "../common/types/languageTypes";
import { ORDERTYPE } from "../common/types/productOrderTypes";
import type { Product } from "../common/types/productTypes";
import type { SiteConfig } from "../common/types/themeTypes";
import { isProductNutrition } from "../common/utils/productUtils";

const ALGOLIA_APPID = "CV5266SV9V";
const ALGOLIA_SEARCHKEY = "6e0ed776d4e102db935498659894446c";
const algoliaEnvironment = process.env.ENV_NAME === "prod" ? "prod" : "preprod";

const desktopLogoUrl = new URL("/src/img/themes/isdalen/Isdalen_premium_ICECREAM_CMYK_positiv.svg", import.meta.url);
const mobileLogoUrl = new URL("/src/img/themes/isdalen/Isdalen_premium_ICECREAM_negativ_positiv.svg", import.meta.url);

const enhanceIsdalenProduct = (product: Product): Product => {
   // Additional mappings based on product name or external category reference
   if (product.size === "-") {
      product.size = undefined;
   }

   if (product.category?.externalReference === "I0041" || product.category?.externalReference === "I0061") {
      product.type = "Tilbehør";
   }

   if (product.category?.externalReference === "I0140") {
      product.type = "Frukt";
   }

   if (product.size === null && includes(product.name, "5L")) {
      product.size = "5 liter";
   }

   if ((product.type === null && includes(product.name, "Eskimo")) || includes(product.name, "Shake")) {
      product.type = "Iskrem";
   }

   // Product data is published with a period as decimal, this in not correct in Norwegian.
   if (isArray(product.nutritions)) {
      if (!isProductNutrition(product.nutritions)) {
         for (const nutrition of product.nutritions) {
            nutrition.value = nutrition.value.replace(/\./g, ",");
         }
      }
   }

   return product;
};

const isdalenSiteConfig: SiteConfig = {
   storeId: "isdalen",
   storeCompanyName: "Isdalen",
   orderSupportEmail: "post@isdalen.no",
   baseUrl: "",
   siteName: "Isdalen Handel",
   m3CompanyNumber: "500",
   m3DivisionNumber: "500",
   productDropdownEnabled: false,
   showComparisonPrice: false,
   defaultOrderType: ORDERTYPE.WEB,
   storeLogo: {
      searchbar: {
         url: desktopLogoUrl.toString(),
         width: 150
      },
      searchbarMobile: {
         url: mobileLogoUrl.toString(),
         width: 80
      },
      footer: {
         url: mobileLogoUrl.toString(),
         width: 140,
         mobileWidth: 90
      }
   },
   faqSlug: "/kundeservice/ofte-stilte-sporsmal",
   filterAttributes: () => ({
      type: "Type",
      size: "Størrelse"
   }),
   papirflyApsCustomerId: "12590",
   papirflyApsImagePrefix: "isd-",
   enhanceProduct: enhanceIsdalenProduct,
   tipApiPrefix: "is-",
   fetchCustomerFeatures: async () => {
      return Promise.resolve([
         { name: FEATURE_NAME.subscriptionOrderAvailable, description: "Makes subscriptions available" },
         { name: FEATURE_NAME.invoicePaymentAvailable, description: "Makes invoice overview available" },
         { name: FEATURE_NAME.createOrderAvailable, description: "Allows customer to place orders" }
      ] satisfies CustomerFeature[]);
   },
   invoiceTooltipText: (_invoice: Invoice) => "",
   deliveryFee: {
      enabled: false
   },
   productAvailability: {
      enabled: true
   },
   newCustomerForm: "https://forms.office.com/e/SyiwHskSJT",
   deliveryNotes: {
      visible: true
   },
   searchConfig: {
      algoliaConfig: {
         appId: ALGOLIA_APPID,
         searchApiKey: ALGOLIA_SEARCHKEY
      },
      searchDomains: [
         {
            type: "product",
            name: "produkter",
            displayName: (locale: SupportedLocaleType) => (locale === "en-US" ? "Products" : "Produkter"),
            homePath: (_locale) => "/produkter.html",
            indexName: `products_${algoliaEnvironment}`,
            linkText: (_locale) => "Se alle produkter"
         },
         {
            type: "v2_article",
            name: "inspirasjon",
            displayName: (_locale: SupportedLocaleType) => "Nyheter",
            homePath: () => "/nyheter",
            indexName: `contentful_${CONTENTFUL_ENV}`,
            linkText: () => "Se alle nyheter"
         }
      ]
   },
   contentImages: {
      cloudinaryMapping: "isdalen_images"
   },
   contactForm: {
      apiPath: "/api/is-contactform",
      reasons: [
         { value: "delivery", label: "Spørsmål angående levering", label_en: "Questions about ordering" },
         { value: "invoice", label: "Endring av fakturaadresse", label_en: "Invoice address changes" },
         { value: "invoice_credit", label: "Spørsmål angående faktura", label_en: "Invoice inquiries" },
         { value: "other", label: "Annet", label_en: "Other" },
         { value: "product_order", label: "Spørsmål angående bestillling", label_en: "Questions about ordering" },
         { value: "product_question", label: "Spørsmål angående produkter", label_en: "Question about products" },
         {
            value: "refrigeration_equipment",
            label: "Spørsmål angående kjølemøbler",
            label_en: "Questions about refrigeration equipment"
         }
      ]
   },
   whileYouWaitMessage: "Lag deg en cola-slush og nyt et par sekunders velfortjent pause!",
   notifications: {
      categories: [
         { id: "ORDER_CONFIRMATION", name: "Ordrebekreftelse" },
         { id: "DELIVERY_CONFIRMATION", name: "Leveringsbekreftelse" },
         { id: "IMPORTANT_INFO_ABOUT_DELIVERY", name: "Viktig melding om levering" }
      ],
      enableDeliveryMessages: false
   },
   orderConfirmation: {
      headerSubTitle: "Du vil motta en bestillingsbekreftelse på e-post om kort tid."
   },
   cart: {
      customerReferenceLabel: "Din betalingsreferanse"
   },
   promotionsPage: {
      enabled: false
   }
};

export default isdalenSiteConfig;
